<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  statusesForCategory,
  statusLabel,
  statusColorClasses,
} from 'dashboard/modules/kanban/constants/statusByCategory';

const props = defineProps({
  card: { type: Object, required: true },
});

const emit = defineEmits(['statusChange', 'columnChange', 'cardClick']);

const { t } = useI18n();

const pipelineCategory = computed(() => props.card.pipeline?.category);

const statusOptions = computed(() =>
  statusesForCategory(pipelineCategory.value).map(status => ({
    value: status,
    label: statusLabel(pipelineCategory.value, status),
  }))
);

const columnOptions = computed(() =>
  (props.card.columns ?? [])
    .filter(c => c.column_type !== 'won' && c.column_type !== 'lost')
    .concat(
      (props.card.columns ?? []).filter(
        c => c.column_type === 'won' || c.column_type === 'lost'
      )
    )
    .map(c => ({ value: c.id, label: c.name }))
);

const statusBadgeClass = computed(() => {
  const { badgeBg, badgeText } = statusColorClasses(
    pipelineCategory.value,
    props.card.status
  );
  return `${badgeBg} ${badgeText}`;
});

const formattedDate = computed(() => {
  const d = props.card.closing_date;
  if (!d) return null;
  const [y, m, day] = d.split('-').map(Number);
  return new Date(y, m - 1, day).toLocaleDateString('pt-BR');
});

function formatMeetingDateTime(timestamp) {
  if (!timestamp) return '';
  return new Date(timestamp * 1000).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const nextMeetingColorClass = computed(() => {
  if (!props.card.next_meeting) return '';
  const meetingDate = new Date(props.card.next_meeting.due_date * 1000);
  const today = new Date();

  if (
    meetingDate.getDate() === today.getDate() &&
    meetingDate.getMonth() === today.getMonth() &&
    meetingDate.getFullYear() === today.getFullYear()
  ) {
    return 'bg-[#12B76A]'; // Verde (Hoje)
  }
  return 'bg-[#7B46F6]'; // Roxo (Futuro)
});
</script>

<template>
  <div class="flex flex-col gap-2 py-3 border-b border-n-weak last:border-b-0">
    <!-- Pipeline header -->
    <div class="flex items-center justify-between">
      <span class="text-sm font-semibold text-n-slate-12">
        {{ card.pipeline?.name }}
      </span>
      <div class="flex items-center gap-1.5">
        <span
          class="text-[11px] font-medium px-2 py-0.5 rounded-full"
          :class="statusBadgeClass"
        >
          {{ statusOptions.find(s => s.value === card.status)?.label }}
        </span>
        <button
          class="text-n-slate-9 hover:text-n-slate-12 transition-colors"
          :title="t('KANBAN.CARD.EDIT_OPPORTUNITY')"
          @click="emit('cardClick', card)"
        >
          <span class="i-lucide-pencil size-3.5" />
        </button>
      </div>
    </div>

    <!-- Status -->
    <div class="grid grid-cols-[140px,1fr] items-center gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('KANBAN.CONTACT_SECTION.STATUS_LABEL') }}
      </span>
      <select
        class="text-sm border border-n-weak rounded-md px-2 py-1 bg-n-surface-2 text-n-slate-12 outline-none focus:border-n-brand w-full"
        :value="card.status"
        @change="
          emit('statusChange', {
            card,
            targetStatus: $event.target.value,
          })
        "
      >
        <option
          v-for="opt in statusOptions"
          :key="opt.value"
          :value="opt.value"
        >
          {{ opt.label }}
        </option>
      </select>
    </div>

    <!-- Fase -->
    <div class="grid grid-cols-[140px,1fr] items-center gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('KANBAN.CONTACT_SECTION.STAGE_LABEL') }}
      </span>
      <select
        class="text-sm border border-n-weak rounded-md px-2 py-1 bg-n-surface-2 text-n-slate-12 outline-none focus:border-n-brand w-full"
        :value="card.column_id"
        @change="
          emit('columnChange', {
            card,
            columnId: Number($event.target.value),
          })
        "
      >
        <option
          v-for="col in columnOptions"
          :key="col.value"
          :value="col.value"
        >
          {{ col.label }}
        </option>
      </select>
    </div>

    <!-- Data de fechamento -->
    <div class="grid grid-cols-[140px,1fr] items-center gap-2">
      <span class="text-sm font-medium text-n-slate-12">
        {{ t('KANBAN.CONTACT_SECTION.SCHEDULING_LABEL') }}
      </span>
      <span class="text-sm text-n-slate-11">
        {{ formattedDate ?? t('KANBAN.CONTACT_SECTION.NO_DATE') }}
      </span>
    </div>

    <!-- Bloco de Reunião Agendada (Barra no rodapé) -->
    <div
      v-if="card.next_meeting"
      class="flex items-center justify-between px-3 py-1.5 text-white rounded-md mt-1"
      :class="nextMeetingColorClass"
    >
      <div class="flex items-center gap-1.5 text-xs font-semibold">
        <span class="i-lucide-calendar-clock size-3.5" />
        {{ t('KANBAN.CALENDAR.NEXT_MEETING') || 'Próxima Reunião' }}
      </div>
      <div
        class="text-[11px] font-bold opacity-100 bg-white/20 px-1.5 py-0.5 rounded"
      >
        {{ formatMeetingDateTime(card.next_meeting.due_date) }}
      </div>
    </div>
  </div>
</template>
